import { useCallback } from 'react';
import { useAuth } from '../../../../hooks/useAuth';
import { useBatchId } from '../../../../hooks/useBatchId';
import { useToastMessage } from '../../../../hooks/useToastMessage';
import {
	DEFAULT_PAGE_INDEX,
	DEFAULT_PAGE_SIZE,
} from '../../../../shared/Constants';
import {
	getRegionsOfScreeningModule,
	getZonesOfScreeningModule,
	updateRegionZoneCenterOfApplication,
} from '../../../../service/VolunteerApplicationService';
import { RegionZoneCenterParams } from '../../../../shared/VolunteerApplicationServiceTypes';
import { displayError } from '../../../../shared/Utils';
import useUserSession from '../../../../hooks/useUserSession';

export const useZoneChange = () => {
	const { srcmId } = useAuth();
	const { selectedRole } = useUserSession();
	const { batchId } = useBatchId();
	const { showToastMessage } = useToastMessage();

	const getAllRegionsDashboard = useCallback(async () => {
		if (srcmId && selectedRole && batchId) {
			try {
				const allRegionsData = await getRegionsOfScreeningModule({
					functionarySrcmId: srcmId,
					batchId,
					pageIndex: DEFAULT_PAGE_INDEX,
					pageSize: DEFAULT_PAGE_SIZE,
					selectedRoleCode: selectedRole,
				});
				return allRegionsData;
			} catch (err) {
				showToastMessage(
					'error',
					err instanceof Error ? err.message : 'Unknown error occurred'
				);
			}
		}
	}, [srcmId, selectedRole, batchId, showToastMessage]);

	const getZonesbyRegionDashboard = useCallback(
		async (regionId: number) => {
			if (srcmId && selectedRole && batchId) {
				try {
					const zonesByRegion = await getZonesOfScreeningModule({
						functionarySrcmId: srcmId,
						batchId,
						regionIds: [regionId],
						pageIndex: DEFAULT_PAGE_INDEX,
						pageSize: DEFAULT_PAGE_SIZE,
						selectedRoleCode: selectedRole,
					});
					return zonesByRegion;
				} catch (err) {
					showToastMessage(
						'error',
						err instanceof Error ? err.message : 'Unknown error occurred'
					);
				}
			}
		},
		[srcmId, selectedRole, batchId, showToastMessage]
	);

	const updateRegionZoneCenterZoneChange = useCallback(
		async (updateRegionZoneCenterParams: RegionZoneCenterParams) => {
			const {
				centerIdToBeUpdated,
				centerNameToBeUpdated,
				regionIdToBeUpdated,
				regionNameToBeUpdated,
				zoneIdToBeUpdated,
				zoneNameToBeUpdated,
				applicationId,
			} = updateRegionZoneCenterParams;
			if (srcmId && batchId) {
				try {
					const zonesByRegion = await updateRegionZoneCenterOfApplication({
						functionarySrcmId: srcmId,
						batchId,
						centerIdToBeUpdated,
						centerNameToBeUpdated,
						regionIdToBeUpdated,
						regionNameToBeUpdated,
						zoneIdToBeUpdated,
						zoneNameToBeUpdated,
						applicationId,
					});
					return zonesByRegion;
				} catch (err) {
					const errorMsg = displayError(err);
					showToastMessage('error', errorMsg);
				}
			}
		},
		[srcmId, batchId, showToastMessage]
	);

	return {
		getAllRegionsDashboard,
		getZonesbyRegionDashboard,
		updateRegionZoneCenterZoneChange,
	};
};
